// src/pages/KarigarManagement/Job/JobCreatePage.jsx
// REFACTORED VERSION - Using shared components and hooks

import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Controller } from "react-hook-form";
import { Button } from "@/components/ui-v2/button";
import { Input } from "@/components/ui-v2/input";
import { Label } from "@/components/ui-v2/label";
import { Textarea } from "@/components/ui-v2/textarea";
import { Checkbox } from "@/components/ui-v2/checkbox";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui-v2/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui-v2/select";
import { Switch } from "@/components/ui-v2/switch";
import { Badge } from "@/components/ui-v2/badge";
import { Separator } from "@/components/ui-v2/separator";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  AlertTriangle,
  User,
  Wrench,
  Package,
  Calendar,
  IndianRupee,
  CheckCircle,
  Briefcase,
  FileText,
  Gem,
  Truck,
  MapPin,
  Phone,
  X,
  Loader2,
  ShoppingBag,
  Image as ImageIcon,
} from "lucide-react";
import { format, addDays } from "date-fns";
import { cn } from "@/lib/utils";

// Shared components
import {
  JobFormSteps,
  ProductSelector,
  StoneManager,
  KarigarSelectorCard,
  KarigarSelectionDialog,
} from "./components";
import JobTypeSelection from "./components/JobTypeSelection";
import JobImagesForm from "./components/JobImagesForm";

// Selection components
import UserSelection from "@/pages/User/components/UserSelection";
import OrderSelection from "@/pages/Orders/components/OrderSelection";
import ProductSelection from "@/components/products/ProductSelection";

// Hooks and utilities
import useJobForm from "./hooks/useJobForm";
import {
  WORK_TYPES,
  PRIORITIES,
  METAL_TYPES,
  PRODUCT_TYPES,
  GOLD_PURITIES,
  SILVER_PURITIES,
  LABOR_RATE_TYPES,
  ITEM_CONDITIONS,
  JOB_TYPES,
  STONE_TYPES,
  FORM_STEPS,
} from "./constants/jobConstants";

/**
 * Job Create Page - Multi-step form for creating new jobs
 */
const JobCreatePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const orderId = searchParams.get("orderId");
  const orderItemIndex = searchParams.get("orderItemIndex");

  // Use the shared job form hook
  const jobForm = useJobForm({
    mode: "create",
    initialJob: null,
    redirectPath: "/karigar-management/jobs",
  });

  const {
    form,
    errors,
    handleSubmit,
    currentStep,
    goToNextStep,
    goToPrevStep,
    canProceedToNext,
    submitting,
    loading,

    // Watch values
    watchJobType,
    watchMetalType,
    watchIsUrgent,

    // Derived state
    needsItemDetails,
    needsCustomer,
    canSelectProduct,

    // Karigar
    selectedKarigar,
    setSelectedKarigar,
    suggestedKarigars,
    showKarigarDialog,
    setShowKarigarDialog,

    // Stones
    stones,
    addStone,
    updateStone,
    removeStone,

    // Images
    referenceImages,
    setReferenceImages,
    itemImages,
    setItemImages,
    productImageUrls,
    removedProductImageIndexes,
    activeProductImages,
    handleRemoveProductImage,
    handleRestoreAllProductImages,
    handleRemoveAllProductImages,
    previewImage,
    setPreviewImage,

    // User
    selectedUser,
    handleUserSelected,
    clearSelectedUser,
    showUserDialog,
    setShowUserDialog,

    // Order
    selectedOrder,
    handleOrderSelected,
    clearSelectedOrder,
    showOrderDialog,
    setShowOrderDialog,

    // Product
    selectedProduct,
    handleProductSelected,
    clearSelectedProduct,
    showProductDialog,
    setShowProductDialog,
  } = jobForm;

  const { register, control, setValue, watch, getValues } = form;

  // ============================================
  // RENDER STEP 2: Job Details
  // ============================================
  const renderStep2 = () => (
    <div className="space-y-6">
      {/* Product Selection Card */}
      {canSelectProduct && (
        <ProductSelector
          selectedProduct={selectedProduct}
          onOpenDialog={() => setShowProductDialog(true)}
          onClear={clearSelectedProduct}
          productImageUrls={productImageUrls}
          removedProductImageIndexes={removedProductImageIndexes}
          stones={stones}
          suggestedKarigars={suggestedKarigars}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Link to Order Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="h-5 w-5" />
              Link to Order (Optional)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedOrder ? (
              <div className="flex items-center justify-between p-4 border rounded-lg bg-blue-50 border-blue-200">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Package className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-blue-800">
                      {selectedOrder.orderId}
                    </p>
                    <p className="text-sm text-blue-600">
                      {selectedOrder.userId?.name ||
                        selectedOrder.guestCustomer?.name ||
                        "Customer"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowOrderDialog(true)}
                  >
                    Change
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSelectedOrder}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full h-20 border-dashed"
                onClick={() => setShowOrderDialog(true)}
              >
                <div className="flex flex-col items-center gap-2">
                  <Package className="h-6 w-6 text-muted-foreground" />
                  <span className="text-sm">Select Order</span>
                </div>
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Customer Selection Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-5 w-5" />
              Customer{" "}
              {needsCustomer && <span className="text-red-500">*</span>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {selectedUser ? (
              <div className="flex items-center justify-between p-4 border rounded-lg bg-green-50 border-green-200">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center text-lg font-semibold text-green-700">
                    {selectedUser.name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">
                      {selectedUser.name}
                    </p>
                    <p className="text-sm text-green-600 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      {selectedUser.phoneNumber}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUserDialog(true)}
                  >
                    Change
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSelectedUser}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className={cn(
                  "w-full h-20 border-dashed",
                  needsCustomer && errors.customerName && "border-red-500"
                )}
                onClick={() => setShowUserDialog(true)}
              >
                <div className="flex flex-col items-center gap-2">
                  <User className="h-6 w-6 text-muted-foreground" />
                  <span className="text-sm">Select or Create Customer</span>
                </div>
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Basic Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Job Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="title">
                Job Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                {...register("title", { required: "Title is required" })}
                placeholder="e.g., Gold Necklace Making"
                className={cn(errors.title && "border-red-500")}
              />
              {errors.title && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.title.message}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Detailed job description..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Work Type</Label>
                <Controller
                  name="workType"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WORK_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              <div>
                <Label>Priority</Label>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITIES.map((p) => (
                          <SelectItem key={p.value} value={p.value}>
                            <Badge
                              variant="outline"
                              className={cn("text-xs", p.color)}
                            >
                              {p.label}
                            </Badge>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            </div>

            <div
              className={cn(
                "flex items-center justify-between p-3 rounded-lg border transition-colors",
                watchIsUrgent ? "bg-red-50 border-red-200" : "bg-muted/50"
              )}
            >
              <div className="flex items-center gap-3">
                <AlertTriangle
                  className={cn(
                    "h-5 w-5",
                    watchIsUrgent ? "text-red-500" : "text-muted-foreground"
                  )}
                />
                <div>
                  <p className="font-medium text-sm">Mark as Urgent</p>
                  <p className="text-xs text-muted-foreground">
                    Needs immediate attention
                  </p>
                </div>
              </div>
              <Controller
                name="isUrgent"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Item Details (for repair/alteration) */}
        {needsItemDetails && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="h-5 w-5" />
                Item Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="itemDescription">
                  Item Description <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="itemDescription"
                  {...register("itemDetails.description", {
                    required: needsItemDetails
                      ? "Item description is required"
                      : false,
                  })}
                  placeholder="Describe the item..."
                  rows={2}
                  className={cn(
                    errors.itemDetails?.description && "border-red-500"
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Item Type</Label>
                  <Controller
                    name="itemDetails.itemType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PRODUCT_TYPES.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label>Metal Type</Label>
                  <Controller
                    name="itemDetails.metalType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select metal" />
                        </SelectTrigger>
                        <SelectContent>
                          {METAL_TYPES.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              <span className="mr-2">{m.icon}</span>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Condition</Label>
                  <Controller
                    name="itemDetails.condition"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {ITEM_CONDITIONS.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              <Badge
                                variant="outline"
                                className={cn("text-xs", c.color)}
                              >
                                {c.label}
                              </Badge>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="itemWeight">Est. Weight (g)</Label>
                  <Input
                    id="itemWeight"
                    type="number"
                    step="0.01"
                    {...register("itemDetails.estimatedWeight")}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  // ============================================
  // RENDER STEP 3: Specifications
  // ============================================
  const renderStep3 = () => (
    <div className="space-y-6">
      {selectedProduct && (
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg flex items-center gap-3">
          <ShoppingBag className="h-5 w-5 text-purple-600" />
          <div>
            <p className="font-medium text-purple-800">
              Specifications populated from: {selectedProduct.name}
            </p>
            <p className="text-sm text-purple-600">
              Review and edit as needed. All fields are editable.
            </p>
          </div>
        </div>
      )}

      {!needsItemDetails ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Product Specifications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Metal Type</Label>
                  <Controller
                    name="specifications.metalType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select metal" />
                        </SelectTrigger>
                        <SelectContent>
                          {METAL_TYPES.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              <span className="mr-2">{m.icon}</span>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label>Product Type</Label>
                  <Controller
                    name="specifications.productType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {PRODUCT_TYPES.map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Purity</Label>
                  <Controller
                    name="specifications.purity"
                    control={control}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select purity" />
                        </SelectTrigger>
                        <SelectContent>
                          {(watchMetalType === "SILVER"
                            ? SILVER_PURITIES
                            : GOLD_PURITIES
                          ).map((p) => (
                            <SelectItem key={p.value} value={p.value}>
                              {p.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>

                <div>
                  <Label htmlFor="weight">Target Weight (g)</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.01"
                    {...register("specifications.weight")}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="size">Size (if applicable)</Label>
                <Input
                  id="size"
                  {...register("specifications.size")}
                  placeholder="e.g., 16 inch, Ring size 12"
                />
              </div>

              <div>
                <Label htmlFor="designReference">Design Reference</Label>
                <Input
                  id="designReference"
                  {...register("specifications.designReference")}
                  placeholder="Design code, image reference, etc."
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <Label
                  htmlFor="hallmarkRequired"
                  className="cursor-pointer font-normal"
                >
                  Hallmark Required
                </Label>
                <Controller
                  name="specifications.hallmarkRequired"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <StoneManager
            stones={stones}
            onAdd={addStone}
            onUpdate={updateStone}
            onRemove={removeStone}
            isFromProduct={!!selectedProduct?.hasStones}
          />
        </div>
      ) : (
        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5" />
              Work Requirements
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="specialInstructions">
                Detailed Work Instructions
              </Label>
              <Textarea
                id="specialInstructions"
                {...register("specialInstructions")}
                placeholder="Describe exactly what needs to be done..."
                rows={6}
              />
            </div>

            <Separator />

            <div className="space-y-3">
              <Label>Additional Requirements</Label>
              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Controller
                  name="requiresPickup"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="requiresPickup"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <div>
                  <Label
                    htmlFor="requiresPickup"
                    className="cursor-pointer font-normal"
                  >
                    Requires Pickup
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Item needs to be picked up
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                <Controller
                  name="requiresDelivery"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="requiresDelivery"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <div>
                  <Label
                    htmlFor="requiresDelivery"
                    className="cursor-pointer font-normal"
                  >
                    Requires Delivery
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Finished item needs to be delivered
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );

  // ============================================
  // RENDER STEP 5: Timeline & Pricing
  // ============================================
  const renderStep5 = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="expectedCompletionDate">
              Expected Completion Date <span className="text-red-500">*</span>
            </Label>
            <Input
              id="expectedCompletionDate"
              type="date"
              {...register("expectedCompletionDate", {
                required: "Expected date is required",
              })}
              min={format(new Date(), "yyyy-MM-dd")}
              className={cn(errors.expectedCompletionDate && "border-red-500")}
            />
            {selectedProduct?.manufacturing?.estimatedMakingTime?.default && (
              <p className="text-xs text-purple-600 mt-1">
                💡 Suggested based on product: ~
                {Math.ceil(
                  selectedProduct.manufacturing.estimatedMakingTime.default / 8
                )}{" "}
                days
              </p>
            )}
          </div>

          <div>
            <Label htmlFor="estimatedHours">Estimated Hours</Label>
            <Input
              id="estimatedHours"
              type="number"
              step="0.5"
              {...register("estimatedHours")}
              placeholder="0"
            />
          </div>

          <div>
            <Label className="text-xs text-muted-foreground">
              Quick Select
            </Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {[3, 5, 7, 14, 30].map((days) => (
                <Button
                  key={days}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setValue(
                      "expectedCompletionDate",
                      format(addDays(new Date(), days), "yyyy-MM-dd")
                    )
                  }
                >
                  {days} days
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IndianRupee className="h-5 w-5" />
            Pricing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Labor Rate Type</Label>
            <Controller
              name="pricing.laborRateType"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABOR_RATE_TYPES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="laborRate">Labor Rate</Label>
              <Input
                id="laborRate"
                type="number"
                step="0.01"
                {...register("pricing.laborRate")}
                placeholder="0"
              />
            </div>

            <div>
              <Label htmlFor="estimatedLabor">Estimated Total (₹)</Label>
              <Input
                id="estimatedLabor"
                type="number"
                {...register("pricing.estimatedLabor")}
                placeholder="0"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <KarigarSelectorCard
        selectedKarigar={selectedKarigar}
        onClear={() => setSelectedKarigar(null)}
        onOpenDialog={() => setShowKarigarDialog(true)}
        suggestedKarigars={suggestedKarigars}
      />
    </div>
  );

  // ============================================
  // RENDER STEP 6: Review
  // ============================================
  const renderStep6 = () => {
    const values = getValues();
    const jobTypeInfo = JOB_TYPES.find((t) => t.value === values.jobType);
    const priorityInfo = PRIORITIES.find((p) => p.value === values.priority);

    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-xl font-semibold">Review & Create</h2>
          <p className="text-muted-foreground">
            Verify the details before creating the job
          </p>
        </div>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {jobTypeInfo && (
                  <div
                    className={cn(
                      "h-10 w-10 rounded-lg flex items-center justify-center",
                      jobTypeInfo.color
                    )}
                  >
                    <jobTypeInfo.icon className="h-5 w-5" />
                  </div>
                )}
                <div>
                  <CardTitle>{values.title || "Untitled Job"}</CardTitle>
                  <CardDescription>{jobTypeInfo?.label}</CardDescription>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {values.isUrgent && (
                  <Badge variant="destructive">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Urgent
                  </Badge>
                )}
                {priorityInfo && (
                  <Badge variant="outline" className={priorityInfo.color}>
                    {priorityInfo.label}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {values.description && (
              <div>
                <p className="text-sm text-muted-foreground">Description</p>
                <p className="mt-1">{values.description}</p>
              </div>
            )}

            {selectedProduct && (
              <>
                <Separator />
                <div className="flex items-center gap-4 p-3 bg-purple-50 rounded-lg">
                  {selectedProduct.media?.[0]?.url ? (
                    <img
                      src={selectedProduct.media[0].url}
                      alt={selectedProduct.name}
                      className="h-16 w-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-lg bg-purple-100 flex items-center justify-center">
                      <ShoppingBag className="h-8 w-8 text-purple-400" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <ShoppingBag className="h-3 w-3" /> Based on Product
                    </p>
                    <p className="font-medium">{selectedProduct.name}</p>
                    <p className="text-sm text-purple-600">
                      SKU: {selectedProduct.sku || selectedProduct.productId}
                    </p>
                  </div>
                </div>
              </>
            )}

            <Separator />

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {needsCustomer && values.customerName && (
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" /> Customer
                  </p>
                  <p className="font-medium">{values.customerName}</p>
                  <p className="text-sm">{values.customerPhone}</p>
                </div>
              )}

              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Due Date
                </p>
                <p className="font-medium">
                  {values.expectedCompletionDate
                    ? format(
                        new Date(values.expectedCompletionDate),
                        "dd MMM yyyy"
                      )
                    : "-"}
                </p>
              </div>

              {values.pricing?.estimatedLabor > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <IndianRupee className="h-3 w-3" /> Est. Labor
                  </p>
                  <p className="font-medium">
                    ₹{values.pricing.estimatedLabor}
                  </p>
                </div>
              )}

              {selectedKarigar && (
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" /> Assigned To
                  </p>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{selectedKarigar.name}</p>
                    {selectedKarigar.isFromProduct && (
                      <Badge className="bg-amber-100 text-amber-800 text-xs">
                        Product
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm">{selectedKarigar.karigarCode}</p>
                </div>
              )}
            </div>

            {(activeProductImages.length > 0 ||
              referenceImages.length > 0 ||
              itemImages.length > 0) && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                    <ImageIcon className="h-3 w-3" /> Images
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {activeProductImages.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="bg-purple-100 text-purple-700"
                      >
                        <ShoppingBag className="h-3 w-3 mr-1" />
                        {activeProductImages.length} Product Image(s)
                      </Badge>
                    )}
                    {referenceImages.length > 0 && (
                      <Badge variant="secondary">
                        {referenceImages.length} Uploaded Reference(s)
                      </Badge>
                    )}
                    {itemImages.length > 0 && (
                      <Badge variant="secondary">
                        {itemImages.length} Item Image(s)
                      </Badge>
                    )}
                  </div>
                </div>
              </>
            )}

            {stones.length > 0 && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                    <Gem className="h-3 w-3" /> Stones ({stones.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {stones.map((stone, i) => (
                      <Badge key={i} variant="outline">
                        {STONE_TYPES.find((s) => s.value === stone.type)
                          ?.label || stone.type}{" "}
                        ×{stone.quantity}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="flex flex-wrap gap-2">
              {values.requiresPickup && (
                <Badge variant="secondary">
                  <Truck className="h-3 w-3 mr-1" /> Requires Pickup
                </Badge>
              )}
              {values.requiresDelivery && (
                <Badge variant="secondary">
                  <MapPin className="h-3 w-3 mr-1" /> Requires Delivery
                </Badge>
              )}
              {values.specifications?.hallmarkRequired && (
                <Badge variant="secondary">
                  <CheckCircle className="h-3 w-3 mr-1" /> Hallmark Required
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Create New Job</h1>
            <p className="text-muted-foreground">
              Step {currentStep} of {FORM_STEPS.length}
            </p>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <JobFormSteps currentStep={currentStep} />

      {/* Step Content */}
      <form onSubmit={handleSubmit}>
        <div className="min-h-[400px]">
          {currentStep === 1 && (
            <JobTypeSelection
              value={watchJobType}
              onChange={(value) => setValue("jobType", value)}
            />
          )}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && (
            <JobImagesForm
              productImageUrls={productImageUrls}
              removedProductImageIndexes={removedProductImageIndexes}
              activeProductImages={activeProductImages}
              onRemoveProductImage={handleRemoveProductImage}
              onRestoreAllProductImages={handleRestoreAllProductImages}
              onRemoveAllProductImages={handleRemoveAllProductImages}
              referenceImages={referenceImages}
              onReferenceImagesChange={setReferenceImages}
              itemImages={itemImages}
              onItemImagesChange={setItemImages}
              previewImage={previewImage}
              onPreviewImage={setPreviewImage}
              onClosePreview={() => setPreviewImage(null)}
              needsItemDetails={needsItemDetails}
              isEditMode={false}
            />
          )}
          {currentStep === 5 && renderStep5()}
          {currentStep === 6 && renderStep6()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between pt-6 border-t mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={goToPrevStep}
            disabled={currentStep === 1}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>

            {currentStep < 6 ? (
              <Button
                type="button"
                onClick={goToNextStep}
                disabled={!canProceedToNext()}
              >
                Next
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button type="submit" disabled={submitting || loading?.create}>
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Create Job
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </form>

      {/* Dialogs */}
      <KarigarSelectionDialog
        open={showKarigarDialog}
        onOpenChange={setShowKarigarDialog}
        selectedKarigar={selectedKarigar}
        onSelect={setSelectedKarigar}
        jobType={watchJobType}
        productType={watch("specifications.productType")}
        metalType={watchMetalType}
        expectedDate={watch("expectedCompletionDate")}
        suggestedKarigars={suggestedKarigars}
        productId={selectedProduct?._id}
      />

      <UserSelection
        isOpen={showUserDialog}
        onClose={() => setShowUserDialog(false)}
        onUserSelected={handleUserSelected}
        existingUser={selectedUser}
        title="Select Customer"
        mode="smart"
        allowSearch={true}
        allowCreate={true}
        allowEdit={true}
        showEmail={true}
        showAddress={false}
        showKYC={false}
        showGSTIN={false}
      />

      <OrderSelection
        isOpen={showOrderDialog}
        onClose={() => setShowOrderDialog(false)}
        onOrderSelected={handleOrderSelected}
        selectedOrder={selectedOrder}
        title="Link to Order"
        showItemSelection={true}
        filterByKarigarWork={false}
      />

      <ProductSelection
        isOpen={showProductDialog}
        onClose={() => setShowProductDialog(false)}
        onProductSelected={handleProductSelected}
        mode="browse"
        title="Select Product for Job"
        multiSelect={false}
        showPricing={false}
        showCheckbox={false}
        filterByStatus="ACTIVE"
        allowCreate={false}
        allowEdit={false}
      />
    </div>
  );
};

export default JobCreatePage;
